import { useState, useRef, useMemo, type DragEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Upload, Download, Trash2, FileText, Eye, AlertCircle, Plus,
  Loader2, ExternalLink, ChevronRight, ChevronDown, Folder,
  FolderOpen, FileSpreadsheet, FileImage, Presentation,
  File, Link2, FolderPlus, X, CalendarPlus, FolderInput, Share2,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { PortalVisibilityPicker } from "@/components/custom/PortalVisibilityPicker";
import { useAuth } from "@/lib/authContext";
import { isFileDrag, filesFromList, summarizeUploads, type UploadFailure } from "@/lib/documentDrop";
import {
  getDocumentFolders,
  createDocumentFolder,
  scaffoldDocumentFolders,
  deleteDocumentFolder,
  addYearToFolder,
  getClientDocuments,
  getDocumentUploadUrl,
  recordDocument,
  getDocumentDownloadUrl,
  updateClientDocument,
  deleteClientDocument,
  getDocumentTrash,
  restoreClientDocument,
  getDocumentRequests,
  createDocumentRequest,
  updateDocumentRequest,
  deleteDocumentRequest,
  requestDocumentDelete,
  getDeleteRequests,
  approveDeleteRequest,
  rejectDeleteRequest,
  cancelDeleteRequest,
  generateDocumentUploadToken,
  fetchWithAuth,
  type DocumentFolder,
  type ClientDocument,
  type DocumentRequest,
  type DeleteRequest,
} from "@/lib/api";

// ── File type helpers ─────────────────────────────────────────────────────────
function getFileCategory(fileName: string, contentType: string | null): "pdf" | "excel" | "word" | "image" | "ppt" | "other" {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf" || contentType?.includes("pdf")) return "pdf";
  if (["xls","xlsx","csv"].includes(ext) || contentType?.includes("spreadsheet") || contentType?.includes("excel")) return "excel";
  if (["doc","docx"].includes(ext) || contentType?.includes("word")) return "word";
  if (["ppt","pptx"].includes(ext) || contentType?.includes("presentation")) return "ppt";
  if (["png","jpg","jpeg","gif","webp","svg","tif","tiff"].includes(ext) || contentType?.startsWith("image/")) return "image";
  return "other";
}

function FileTypeIcon({ fileName, contentType, size = "sm" }: { fileName: string; contentType: string | null; size?: "sm" | "md" }) {
  const cls = size === "md" ? "w-5 h-5" : "w-4 h-4";
  const cat = getFileCategory(fileName, contentType);
  if (cat === "pdf")   return <FileText className={cn(cls, "text-red-500")} />;
  if (cat === "excel") return <FileSpreadsheet className={cn(cls, "text-green-600")} />;
  if (cat === "word")  return <FileText className={cn(cls, "text-blue-600")} />;
  if (cat === "ppt")   return <Presentation className={cn(cls, "text-orange-500")} />;
  if (cat === "image") return <FileImage className={cn(cls, "text-violet-500")} />;
  return <File className={cn(cls, "text-slate-400")} />;
}

const OFFICE_EXT = new Set(["doc","docx","xls","xlsx","ppt","pptx","csv"]);
function canPreview(fileName: string, contentType: string | null): boolean {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (contentType?.includes("pdf") || ext === "pdf") return true;
  if (contentType?.startsWith("image/") || ["png","jpg","jpeg","gif","webp","svg"].includes(ext)) return true;
  if (OFFICE_EXT.has(ext)) return true;
  return false;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// ── R2 banner ─────────────────────────────────────────────────────────────────
function R2Banner() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
      <div>
        <p className="font-semibold">Document storage not yet configured</p>
        <p className="text-xs mt-0.5 text-amber-700">
          Add <code className="font-mono bg-amber-100 px-1 rounded">R2_ACCOUNT_ID</code>,{" "}
          <code className="font-mono bg-amber-100 px-1 rounded">R2_ACCESS_KEY_ID</code>,{" "}
          <code className="font-mono bg-amber-100 px-1 rounded">R2_SECRET_ACCESS_KEY</code>, and{" "}
          <code className="font-mono bg-amber-100 px-1 rounded">R2_BUCKET_NAME</code> to the server <code className="font-mono bg-amber-100 px-1 rounded">.env</code> to enable uploads.
        </p>
      </div>
    </div>
  );
}

// ── Preview modal ─────────────────────────────────────────────────────────────
function PreviewModal({ url, fileName, contentType, onClose }: {
  url: string; fileName: string; contentType: string | null; onClose: () => void;
}) {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  const isPdf   = contentType?.includes("pdf") || ext === "pdf";
  const isImage = contentType?.startsWith("image/") || ["png","jpg","jpeg","gif","webp","svg"].includes(ext);
  const isOffice = OFFICE_EXT.has(ext) && !isPdf && !isImage;
  const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-full h-[90vh] flex flex-col p-0 gap-0">
        <div className="flex items-center justify-between px-4 py-2 border-b shrink-0">
          <span className="text-sm font-medium truncate">{fileName}</span>
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 shrink-0 ml-4">
            <ExternalLink className="w-3.5 h-3.5" />Open in new tab
          </a>
        </div>
        <div className="flex-1 overflow-hidden">
          {isPdf   ? <iframe src={url} className="w-full h-full border-0" title={fileName} /> :
           isImage ? <div className="flex items-center justify-center h-full bg-slate-100 p-4">
                       <img src={url} alt={fileName} className="max-w-full max-h-full object-contain rounded" />
                     </div> :
           isOffice ? <iframe src={officeUrl} className="w-full h-full border-0" title={fileName} /> :
           <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
             <FileText className="w-12 h-12 opacity-30" />
             <p className="text-sm">Preview not available</p>
             <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline">Open in new tab</a>
           </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Folder tree helpers ───────────────────────────────────────────────────────
function buildTree(flat: DocumentFolder[]): DocumentFolder[] {
  const map = new Map<string, DocumentFolder>();
  flat.forEach((f) => map.set(f.id, { ...f, children: [] }));
  const roots: DocumentFolder[] = [];
  flat.forEach((f) => {
    const node = map.get(f.id)!;
    if (f.parent_id && map.has(f.parent_id)) {
      map.get(f.parent_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

// ── Folder tree node ──────────────────────────────────────────────────────────
function FolderNode({
  folder, level, selectedPath, onSelect, fileCountMap, onAddChild, onAddYear, onDelete, onDropFiles,
}: {
  folder: DocumentFolder;
  level: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  fileCountMap: Map<string, number>;
  onAddChild: (parent: DocumentFolder) => void;
  onAddYear: (folder: DocumentFolder) => void;
  onDelete: (folder: DocumentFolder) => void;
  onDropFiles: (files: File[], folderPath: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const { user: folderUser } = useAuth();
  const canDeleteFolder = folderUser?.role === "manager" || folderUser?.role === "admin";
  const hasChildren = (folder.children?.length ?? 0) > 0;
  const isSelected = selectedPath === folder.path;

  // Count files in this folder and all descendants
  const count = useMemo(() => {
    let n = fileCountMap.get(folder.path) ?? 0;
    function addChildren(f: DocumentFolder) {
      (f.children ?? []).forEach((c) => {
        n += fileCountMap.get(c.path) ?? 0;
        addChildren(c);
      });
    }
    addChildren(folder);
    return n;
  }, [folder, fileCountMap]);

  return (
    <div>
      <div
        className={cn(
          "group flex items-center gap-1 py-1 pr-2 rounded cursor-pointer select-none transition-colors",
          isSelected ? "bg-primary/10 text-primary" : "hover:bg-slate-100 text-foreground",
          dragOver && "ring-2 ring-primary ring-dashed bg-primary/15",
        )}
        style={{ paddingLeft: `${8 + level * 14}px` }}
        onDragOver={(e) => {
          if (!isFileDrag(e.dataTransfer.types)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "copy";
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
        }}
        onDrop={(e) => {
          if (!isFileDrag(e.dataTransfer.types)) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          onDropFiles(filesFromList(e.dataTransfer.files), folder.path);
        }}
      >
        {/* Expand/collapse */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="shrink-0 w-4 h-4 flex items-center justify-center text-muted-foreground/60 hover:text-muted-foreground"
        >
          {hasChildren
            ? (expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />)
            : <span className="w-3" />}
        </button>

        {/* Folder icon */}
        {isSelected || expanded
          ? <FolderOpen className="w-3.5 h-3.5 shrink-0 text-amber-500" />
          : <Folder className="w-3.5 h-3.5 shrink-0 text-amber-400" />}

        {/* Name */}
        <button
          type="button"
          onClick={() => { onSelect(folder.path); if (hasChildren) setExpanded(true); }}
          className="flex-1 text-left text-sm truncate"
        >
          {folder.name}
        </button>

        {/* Green dot when folder (or any descendant) has files */}
        {count > 0 && (
          <span
            className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0"
            title={`${count} file${count !== 1 ? "s" : ""}`}
          />
        )}

        {/* Add year (only on year-accepting root folders) */}
        {YEAR_SCAFFOLD_FOLDERS.has(folder.path) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAddYear(folder); }}
            title="Add year"
            className="opacity-90 shrink-0 p-0.5 rounded hover:bg-amber-100 text-muted-foreground hover:text-amber-700"
          >
            <CalendarPlus className="w-3 h-3" />
          </button>
        )}

        {/* Add subfolder */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAddChild(folder); }}
          title="Add subfolder"
          className="opacity-90 shrink-0 p-0.5 rounded hover:bg-slate-200 text-muted-foreground hover:text-foreground"
        >
          <FolderPlus className="w-3 h-3" />
        </button>

        {/* Delete folder (managers/admins only; the server enforces it too) */}
        {canDeleteFolder && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(folder); }}
            title="Delete folder"
            className="opacity-90 shrink-0 p-0.5 rounded hover:bg-red-100 text-muted-foreground hover:text-red-600"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      {expanded && hasChildren && (
        <div>
          {folder.children!.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              level={level + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
              fileCountMap={fileCountMap}
              onAddChild={onAddChild}
              onAddYear={onAddYear}
              onDelete={onDelete}
              onDropFiles={onDropFiles}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Shared upload (button + drag-and-drop) ───────────────────────────────────
// v1.5.101: three steps, record accepts only the server-issued intentId.
async function uploadDocumentFile(accountId: string, folderPath: string, file: File): Promise<void> {
  const { uploadUrl, intentId } = await getDocumentUploadUrl(
    accountId, file.name, file.type || "application/octet-stream", folderPath, file.size,
  );
  const res = await fetch(uploadUrl, {
    method: "PUT", body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  });
  if (!res.ok) throw new Error("Upload to R2 failed");
  await recordDocument(accountId, { intentId });
}

// Sequential; one failure never stops the rest. Reports one summary toast.
async function uploadDocumentFiles(
  accountId: string, folderPath: string, files: File[],
  onProgress: (done: number, total: number) => void,
): Promise<void> {
  const ok: string[] = [];
  const failed: UploadFailure[] = [];
  onProgress(0, files.length);
  for (const file of files) {
    try {
      await uploadDocumentFile(accountId, folderPath, file);
      ok.push(file.name);
    } catch (err: any) {
      failed.push({ name: file.name, message: err?.message });
    }
    onProgress(ok.length + failed.length, files.length);
  }
  const s = summarizeUploads(ok, failed);
  toast({ title: s.title, description: s.description, variant: s.destructive ? "destructive" : undefined });
}

// ── Upload button ─────────────────────────────────────────────────────────────
function UploadButton({ uploading, progress, onFiles }: {
  uploading: boolean; progress: { done: number; total: number } | null; onFiles: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const label = !uploading
    ? "Upload"
    : progress && progress.total > 1
      ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`
      : "Uploading…";

  return (
    <>
      <input ref={inputRef} type="file" multiple className="hidden"
        onChange={(e) => {
          const files = filesFromList(e.target.files);
          if (files.length) onFiles(files);
          e.target.value = "";
        }} />
      <Button size="sm" className="gap-1.5 h-8" disabled={uploading}
        onClick={() => inputRef.current?.click()}>
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {label}
      </Button>
    </>
  );
}

// ── Document row ──────────────────────────────────────────────────────────────
function DocumentRow({ doc, accountId, folders, onDeleted, onUpdated, showFolder, canDelete, hasPendingDelete, onDeleteRequested }: {
  doc: ClientDocument; accountId: string; folders: DocumentFolder[]; onDeleted: () => void; onUpdated: () => void; showFolder?: boolean;
  canDelete: boolean; hasPendingDelete: boolean; onDeleteRequested: () => void;
}) {
  const [showRequestDelete, setShowRequestDelete] = useState(false);
  const [busy, setBusy] = useState<"view" | "open" | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesVal, setNotesVal] = useState(doc.notes ?? "");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showMove, setShowMove] = useState(false);
  const [confirmPortalCopy, setConfirmPortalCopy] = useState(false);
  const [copyingToPortal, setCopyingToPortal] = useState(false);
  const [rowHidden, setRowHidden] = useState(false);
  const previewa = canPreview(doc.file_name, doc.content_type);

  const alreadyInClientDocs =
    doc.folder_path === "Client Documents" || doc.folder_path.startsWith("Client Documents/");

  async function copyToClientDocs() {
    setCopyingToPortal(true);
    try {
      await updateClientDocument(accountId, doc.id, { folderPath: "Client Documents" });
      toast({ title: "Moved to Client Documents", description: "File is now visible in the client portal." });
      setRowHidden(true);
      onUpdated();
    } catch (err: any) {
      toast({ title: "Failed", description: err?.message, variant: "destructive" });
    } finally { setCopyingToPortal(false); setConfirmPortalCopy(false); }
  }

  if (rowHidden) return null;

  async function openPreview() {
    setBusy("view");
    try {
      const { downloadUrl } = await getDocumentDownloadUrl(accountId, doc.id, "view");
      if (previewa) setPreviewUrl(downloadUrl);
      else window.open(downloadUrl, "_blank");
    } catch (err: any) {
      toast({ title: "Failed to open", description: err?.message, variant: "destructive" });
    } finally { setBusy(null); }
  }

  async function openNative() {
    setBusy("open");
    try {
      const { downloadUrl } = await getDocumentDownloadUrl(accountId, doc.id, "download");
      // Trigger OS-native open via hidden anchor + click
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err: any) {
      toast({ title: "Failed to open", description: err?.message, variant: "destructive" });
    } finally { setBusy(null); }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteClientDocument(accountId, doc.id);
      onDeleted();
    } catch (err: any) {
      toast({ title: "Delete failed", description: err?.message, variant: "destructive" });
    } finally { setDeleting(false); setConfirmDelete(false); }
  }

  async function saveNotes() {
    try {
      await updateClientDocument(accountId, doc.id, { notes: notesVal });
      setEditingNotes(false);
      onUpdated();
    } catch {
      toast({ title: "Failed to save notes", variant: "destructive" });
    }
  }

  return (
    <>
      {previewUrl && (
        <PreviewModal url={previewUrl} fileName={doc.file_name} contentType={doc.content_type}
          onClose={() => setPreviewUrl(null)} />
      )}
      {showRequestDelete && (
        <RequestDeleteDialog
          accountId={accountId}
          doc={doc}
          onDone={() => { setShowRequestDelete(false); onDeleteRequested(); }}
          onClose={() => setShowRequestDelete(false)}
        />
      )}
      {showMove && (
        <MoveFolderDialog
          doc={doc}
          accountId={accountId}
          folders={folders}
          onMoved={() => { setRowHidden(true); onUpdated(); }}
          onClose={() => setShowMove(false)}
        />
      )}
      <div className="group border-b last:border-0 px-3 py-2.5 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <span className="shrink-0"><FileTypeIcon fileName={doc.file_name} contentType={doc.content_type} /></span>

          <div className="flex-1 min-w-0">
            <button onClick={openPreview}
              className="text-sm font-medium text-left truncate hover:text-primary hover:underline block max-w-full"
              title={doc.file_name}>
              {doc.file_name}
            </button>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className="text-[10px] text-muted-foreground">{formatBytes(doc.file_size)}</span>
              <span className="text-[10px] text-muted-foreground">·</span>
              <span className="text-[10px] text-muted-foreground">
                {format(new Date(doc.uploaded_at), "MMM d, yyyy")}
              </span>
              {showFolder && doc.folder_path && (
                <>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <span className="text-[10px] text-muted-foreground/70 italic">{doc.folder_path}</span>
                </>
              )}
              {hasPendingDelete && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">Delete requested</span>
              )}
              {doc.is_client_visible && (
                <>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  {doc.download_count > 0 ? (
                    <span className="text-[10px] text-emerald-700 font-medium" title="Client download receipt">
                      ✓ Client downloaded ×{doc.download_count}
                      {doc.last_downloaded_at && ` (last ${format(new Date(doc.last_downloaded_at), "MMM d")})`}
                    </span>
                  ) : (
                    <span className="text-[10px] text-amber-600" title="Client has not downloaded this file yet">
                      Not yet viewed by client
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Action buttons — always visible, two primary actions */}
          <div className="flex items-center gap-1 shrink-0">
            {doc.is_client_visible && (
              <PortalVisibilityPicker
                accountId={accountId}
                value={doc.visible_to_emails ?? null}
                onChange={async (emails) => {
                  try {
                    await updateClientDocument(accountId, doc.id, { visible_to_emails: emails });
                    onUpdated();
                  } catch (err: any) {
                    toast({ title: "Failed to update visibility", description: err?.message, variant: "destructive" });
                  }
                }}
              />
            )}
            {/* Quick View */}
            <button onClick={openPreview} disabled={busy !== null}
              title="Quick View"
              className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-slate-200 hover:border-slate-400 hover:bg-slate-100 text-muted-foreground hover:text-foreground transition-colors">
              {busy === "view"
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Eye className="w-3 h-3" />}
              <span>View</span>
            </button>

            {/* Open (native app) */}
            <button onClick={openNative} disabled={busy !== null}
              title="Open in native app (downloads file)"
              className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-muted-foreground hover:text-blue-700 transition-colors">
              {busy === "open"
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Download className="w-3 h-3" />}
              <span>Open</span>
            </button>

            {/* Hover-only: Move + portal-copy + delete */}
            <div className="opacity-90 transition-opacity flex items-center gap-1">
              {/* Move to folder */}
              <button
                onClick={() => { setShowMove(true); setConfirmPortalCopy(false); }}
                title="Move to folder"
                className="p-1 rounded hover:bg-slate-200 text-muted-foreground hover:text-foreground"
              >
                <FolderInput className="w-3.5 h-3.5" />
              </button>

              {/* Copy to Client Documents (portal share) — only if not already there */}
              {!alreadyInClientDocs && !confirmPortalCopy && (
                <button
                  onClick={() => setConfirmPortalCopy(true)}
                  title="Share to Client Documents (portal-visible)"
                  className="p-1 rounded hover:bg-blue-100 text-muted-foreground hover:text-blue-600"
                >
                  <Share2 className="w-3.5 h-3.5" />
                </button>
              )}
              {!alreadyInClientDocs && confirmPortalCopy && (
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-amber-700 whitespace-nowrap">Portal-visible?</span>
                  <button
                    onClick={copyToClientDocs}
                    disabled={copyingToPortal}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {copyingToPortal ? "…" : "Yes"}
                  </button>
                  <button
                    onClick={() => setConfirmPortalCopy(false)}
                    className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-slate-100"
                  >
                    No
                  </button>
                </div>
              )}

              {/* Delete (managers/admins) or Request delete (everyone else) */}
              {!canDelete ? (
                hasPendingDelete ? null : (
                  <button onClick={() => setShowRequestDelete(true)} title="Request delete"
                    className="text-[10px] px-1.5 py-0.5 rounded border text-muted-foreground hover:bg-amber-50 hover:text-amber-800">
                    Request delete
                  </button>
                )
              ) : !confirmDelete ? (
                <button onClick={() => setConfirmDelete(true)} title="Delete"
                  className="p-1 rounded hover:bg-red-100 text-muted-foreground hover:text-red-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <button onClick={handleDelete} disabled={deleting}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    {deleting ? "…" : "Delete"}
                  </button>
                  <button onClick={() => setConfirmDelete(false)}
                    className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-slate-100">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        {editingNotes ? (
          <div className="mt-2 flex flex-col gap-1">
            <Textarea value={notesVal} onChange={(e) => setNotesVal(e.target.value)}
              className="text-xs h-16 resize-none" placeholder="Add notes…" />
            <div className="flex gap-1">
              <Button size="sm" className="h-6 text-xs px-2" onClick={saveNotes}>Save</Button>
              <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                onClick={() => { setEditingNotes(false); setNotesVal(doc.notes ?? ""); }}>Cancel</Button>
            </div>
          </div>
        ) : doc.notes ? (
          <p className="mt-1 text-xs text-muted-foreground pl-6 cursor-pointer hover:text-foreground"
            onClick={() => setEditingNotes(true)}>{doc.notes}</p>
        ) : (
          <button onClick={() => setEditingNotes(true)}
            className="mt-0.5 ml-6 text-[10px] text-muted-foreground/50 hover:text-muted-foreground opacity-90 transition-opacity">
            + notes
          </button>
        )}
      </div>
    </>
  );
}

// ── Document request row ──────────────────────────────────────────────────────
function RequestRow({ request, accountId, onUpdated, onDeleted }: {
  request: DocumentRequest; accountId: string; onUpdated: () => void; onDeleted: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const statusColors: Record<string, string> = {
    pending:   "bg-amber-100 text-amber-800",
    fulfilled: "bg-green-100 text-green-800",
    cancelled: "bg-slate-100 text-slate-600",
  };

  async function cancel() {
    try {
      await updateDocumentRequest(accountId, request.id, { status: "cancelled" });
      onUpdated();
    } catch { toast({ title: "Failed to cancel", variant: "destructive" }); }
  }

  async function remove() {
    setDeleting(true);
    try {
      await deleteDocumentRequest(accountId, request.id);
      onDeleted();
    } catch (err: any) {
      toast({ title: "Failed to delete", description: err?.message, variant: "destructive" });
    } finally { setDeleting(false); }
  }

  async function copyLink() {
    setLinkBusy(true);
    try {
      const { uploadUrl } = await generateDocumentUploadToken(accountId, request.id);
      await navigator.clipboard.writeText(uploadUrl);
      setCopied(true);
      toast({ title: "Upload link copied", description: "Send to client. Expires in 7 days." });
      setTimeout(() => setCopied(false), 3000);
    } catch (err: any) {
      toast({ title: "Failed to generate link", description: err?.message, variant: "destructive" });
    } finally { setLinkBusy(false); }
  }

  return (
    <div className="group flex items-start justify-between gap-2 border rounded-lg px-3 py-2 hover:bg-slate-50">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{request.title}</p>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded-full", statusColors[request.status] ?? statusColors.pending)}>
            {request.status}
          </span>
          {request.due_date && (
            <span className="text-[10px] text-muted-foreground">Due {request.due_date}</span>
          )}
          {request.requester && (
            <span className="text-[10px] text-muted-foreground">by {request.requester.name}</span>
          )}
        </div>
        {request.notes && <p className="text-xs text-muted-foreground mt-0.5">{request.notes}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0 opacity-90 transition-opacity">
        {request.status === "pending" && (
          <button onClick={copyLink} disabled={linkBusy}
            className={cn(
              "flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors",
              copied
                ? "border-green-400 text-green-700 bg-green-50"
                : "hover:bg-blue-50 hover:border-blue-300 text-muted-foreground hover:text-blue-700"
            )}>
            {linkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}
            {copied ? "Copied!" : "Copy link"}
          </button>
        )}
        {request.status === "pending" && (
          <button onClick={cancel} className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-slate-200 text-muted-foreground">
            Cancel
          </button>
        )}
        <button onClick={remove} disabled={deleting}
          className="p-1 rounded hover:bg-red-100 text-muted-foreground hover:text-red-600">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Folders that support "Add Year" (auto-scaffolds months or tax subfolders)
const YEAR_SCAFFOLD_FOLDERS = new Set([
  "Bookkeeping", "Tax Returns", "Payroll", "Financial Statements", "Trial Balance & Workpapers", "Z - FirmOps",
]);

// ── Add Year dialog ───────────────────────────────────────────────────────────
function AddYearDialog({ accountId, folder, onCreated, onClose }: {
  accountId: string; folder: DocumentFolder; onCreated: () => void; onClose: () => void;
}) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => currentYear + 2 - i); // +2 to -4
  const [year, setYear] = useState(currentYear);
  const [saving, setSaving] = useState(false);

  const rootName = folder.path.split("/")[0];
  const subfoldersLabel =
    rootName === "Bookkeeping"
      ? "12 months, each with Reconciliations / Statements / Weekly Transactions subfolders"
      : ["Payroll", "Financial Statements", "Z - FirmOps"].includes(rootName)
      ? "12 monthly subfolders (01 - January … 12 - December)"
      : rootName === "Tax Returns"
      ? "Client Copy, Correspondence, e-docs & Vouchers, Firm Copy, Source Documents, Tax Planning"
      : "year folder only";

  async function submit() {
    setSaving(true);
    try {
      await addYearToFolder(accountId, folder.id, year);
      toast({ title: `${year} added to ${folder.name}` });
      onCreated();
      onClose();
    } catch (err: any) {
      toast({ title: "Failed to add year", description: err?.message, variant: "destructive" });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <div className="flex flex-col gap-3 p-1">
          <p className="text-sm font-semibold">Add year to "{folder.name}"</p>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border rounded px-2 py-1.5 text-sm w-full"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <p className="text-xs text-muted-foreground bg-slate-50 rounded px-2 py-1.5">
            Will create: <span className="font-medium">{year}</span> → {subfoldersLabel}
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={saving}>
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              Create
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── New folder dialog ─────────────────────────────────────────────────────────
function NewFolderDialog({ accountId, parent, onCreated, onClose }: {
  accountId: string; parent: DocumentFolder | null; onCreated: () => void; onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createDocumentFolder(accountId, { name: name.trim(), parentId: parent?.id ?? null });
      onCreated();
      onClose();
    } catch (err: any) {
      toast({ title: "Failed to create folder", description: err?.message, variant: "destructive" });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <div className="flex flex-col gap-3 p-1">
          <p className="text-sm font-semibold">
            {parent ? `New subfolder in "${parent.name}"` : "New top-level folder"}
          </p>
          <input
            autoFocus
            className="border rounded px-2 py-1.5 text-sm w-full"
            placeholder="Folder name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onClose(); }}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={!name.trim() || saving}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Create
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Cascading folder picker ───────────────────────────────────────────────────
function CascadingFolderPicker({ folders, initialPath, onChange }: {
  folders: DocumentFolder[];
  initialPath: string;
  onChange: (path: string) => void;
}) {
  const byId = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);

  const childrenOf = useMemo(() => {
    const m = new Map<string | null, DocumentFolder[]>();
    folders.forEach((f) => {
      const k = f.parent_id ?? null;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(f);
    });
    return m;
  }, [folders]);

  function buildChain(targetPath: string): string[] {
    const target = folders.find((f) => f.path === targetPath);
    if (!target) return [];
    const chain: string[] = [];
    let cur: DocumentFolder | undefined = target;
    while (cur) {
      chain.unshift(cur.id);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return chain;
  }

  const [chain, setChain] = useState<string[]>(() => buildChain(initialPath));

  function handleSelect(level: number, value: string) {
    let newChain: string[];
    if (value === "") {
      newChain = chain.slice(0, level);
    } else {
      newChain = [...chain.slice(0, level), value];
    }
    setChain(newChain);
    const targetId = newChain[newChain.length - 1];
    const targetFolder = targetId ? byId.get(targetId) : null;
    onChange(targetFolder?.path ?? "");
  }

  // Build levels: always show L0, then L1 if L0 is selected + has children, etc.
  const levels: { options: DocumentFolder[]; selected: string }[] = [];
  levels.push({ options: childrenOf.get(null) ?? [], selected: chain[0] ?? "" });
  for (let i = 0; i < chain.length; i++) {
    const children = childrenOf.get(chain[i]) ?? [];
    if (children.length > 0) {
      levels.push({ options: children, selected: chain[i + 1] ?? "" });
    } else {
      break;
    }
  }

  const targetId = chain[chain.length - 1] ?? null;
  const targetFolder = targetId ? byId.get(targetId) : null;

  return (
    <div className="flex flex-col gap-2">
      {levels.map((lvl, i) => (
        <div key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />}
          <select
            value={lvl.selected}
            onChange={(e) => handleSelect(i, e.target.value)}
            className="border rounded px-2 py-1.5 text-sm flex-1"
          >
            <option value="">{i === 0 ? "— Select folder —" : "— No subfolder —"}</option>
            {lvl.options.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      ))}
      {targetFolder && (
        <p className="text-[11px] text-muted-foreground">
          Moving to:{" "}
          <span className="font-medium text-foreground">{targetFolder.path}</span>
        </p>
      )}
    </div>
  );
}

// ── Move folder dialog ────────────────────────────────────────────────────────
function MoveFolderDialog({ doc, accountId, folders, onMoved, onClose }: {
  doc: ClientDocument;
  accountId: string;
  folders: DocumentFolder[];
  onMoved: () => void;
  onClose: () => void;
}) {
  const [targetPath, setTargetPath] = useState(doc.folder_path);
  const [saving, setSaving] = useState(false);
  const [confirmPortal, setConfirmPortal] = useState(false);

  const alreadyPortal =
    doc.folder_path === "Client Documents" || doc.folder_path.startsWith("Client Documents/");
  const targetIsClientDocs =
    targetPath === "Client Documents" || targetPath.startsWith("Client Documents/");

  async function doMove() {
    if (!targetPath || targetPath === doc.folder_path) { onClose(); return; }
    setSaving(true);
    try {
      await updateClientDocument(accountId, doc.id, { folderPath: targetPath });
      toast({ title: "File moved", description: `→ ${targetPath}` });
      onMoved();
      onClose();
    } catch (err: any) {
      toast({ title: "Move failed", description: err?.message, variant: "destructive" });
    } finally { setSaving(false); }
  }

  function handleMove() {
    if (targetIsClientDocs && !alreadyPortal && !confirmPortal) {
      setConfirmPortal(true);
    } else {
      doMove();
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <div className="flex flex-col gap-3 p-1">
          <p className="text-sm font-semibold">Move "{doc.file_name}"</p>

          <CascadingFolderPicker
            folders={folders}
            initialPath={doc.folder_path}
            onChange={(path) => { setTargetPath(path); setConfirmPortal(false); }}
          />

          {targetIsClientDocs && !alreadyPortal && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <span className="font-semibold">Portal-visible:</span> Moving here makes this file
              visible to the client in their portal.
            </div>
          )}

          {confirmPortal && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 font-medium">
              Confirm: this file will become visible to the client portal. Continue?
            </div>
          )}

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleMove}
              disabled={saving || !targetPath || targetPath === doc.folder_path}
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
              {confirmPortal ? "Yes, move & share" : "Move here"}
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Scaffold prompt ───────────────────────────────────────────────────────────
function ScaffoldPrompt({ accountId, onDone }: { accountId: string; onDone: () => void }) {
  const [type, setType] = useState<"business" | "individual">("business");
  const [loading, setLoading] = useState(false);

  async function scaffold() {
    setLoading(true);
    try {
      await scaffoldDocumentFolders(accountId, type);
      toast({ title: "Folder structure created" });
      onDone();
    } catch (err: any) {
      toast({ title: "Failed to create folders", description: err?.message, variant: "destructive" });
    } finally { setLoading(false); }
  }

  return (
    <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
      <Folder className="w-10 h-10 text-amber-400" />
      <div>
        <p className="text-sm font-semibold text-slate-700">No folders yet</p>
        <p className="text-xs text-muted-foreground mt-1">Set up the standard folder structure to get started.</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setType("business")}
          className={cn("text-xs px-3 py-1.5 rounded-full border transition-colors",
            type === "business" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-slate-400")}
        >Business</button>
        <button
          onClick={() => setType("individual")}
          className={cn("text-xs px-3 py-1.5 rounded-full border transition-colors",
            type === "individual" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-slate-400")}
        >Individual</button>
      </div>
      <Button size="sm" onClick={scaffold} disabled={loading} className="gap-2">
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Create {type === "business" ? "Business" : "Individual"} Folder Structure
      </Button>
    </div>
  );
}

// ── Delete requests (v1.6.1) ──────────────────────────────────────────────────
function RequestDeleteDialog({ accountId, doc, onDone, onClose }: {
  accountId: string; doc: ClientDocument; onDone: () => void; onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!reason.trim() || saving) return;
    setSaving(true);
    try {
      await requestDocumentDelete(accountId, doc.id, reason.trim());
      toast({ title: "Delete requested", description: "A manager or admin will review it." });
      onDone();
    } catch (err: any) {
      toast({ title: "Could not request delete", description: err?.message, variant: "destructive" });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <div className="flex flex-col gap-3 p-1">
          <p className="text-sm font-semibold">Request delete: "{doc.file_name}"</p>
          <p className="text-xs text-muted-foreground">
            Only managers and admins can delete documents. Tell them why this one should go.
          </p>
          <Textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000}
            className="text-sm h-24 resize-none" placeholder="Reason (required)" />
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={!reason.trim() || saving}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Send request
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRequestRow({ request, canManage, isMine, onChanged }: {
  request: DeleteRequest; canManage: boolean; isMine: boolean; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const pending = request.status === "pending";

  async function run(action: () => Promise<void>, okTitle: string) {
    setBusy(true);
    try {
      await action();
      toast({ title: okTitle });
      onChanged();
    } catch (err: any) {
      toast({ title: "Action failed", description: err?.message, variant: "destructive" });
      onChanged();
    } finally { setBusy(false); setRejecting(false); }
  }

  return (
    <div className="border rounded-lg p-2.5 text-sm flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="flex-1 min-w-0 truncate font-medium" title={request.file_name ?? ""}>{request.file_name ?? "(document)"}</span>
        <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize",
          pending ? "bg-amber-100 text-amber-800"
            : request.status === "approved" ? "bg-red-100 text-red-800"
            : "bg-slate-100 text-slate-700")}>
          {request.status}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {request.folder_path ? `${request.folder_path} · ` : ""}
        {request.requested_by_name ?? "A teammate"} · {format(new Date(request.created_at), "MMM d, yyyy")}
      </p>
      <p className="text-xs">"{request.reason}"</p>
      {request.decision_note && <p className="text-xs text-muted-foreground">Note: {request.decision_note}</p>}
      {pending && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {canManage && !rejecting && (
            <>
              <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={busy}
                onClick={() => run(() => approveDeleteRequest(request.id), "Approved and deleted")}>
                Approve (delete)
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                onClick={() => setRejecting(true)}>
                Reject
              </Button>
            </>
          )}
          {canManage && rejecting && (
            <>
              <input autoFocus className="border rounded px-2 py-1 text-xs bg-white flex-1 min-w-[10rem]"
                placeholder="Note to requester (optional)" maxLength={1000}
                value={note} onChange={(e) => setNote(e.target.value)} />
              <Button size="sm" className="h-7 text-xs" disabled={busy}
                onClick={() => run(() => rejectDeleteRequest(request.id, note.trim()), "Request rejected")}>
                Confirm reject
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setRejecting(false)}>Back</Button>
            </>
          )}
          {isMine && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy}
              onClick={() => run(() => cancelDeleteRequest(request.id), "Request cancelled")}>
              Cancel request
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export function ClientDocuments({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [newFolderParent, setNewFolderParent] = useState<DocumentFolder | "root" | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<DocumentFolder | null>(null);
  const [addYearFolder, setAddYearFolder] = useState<DocumentFolder | null>(null);
  const [showRequests, setShowRequests] = useState(false);
  const [addingRequest, setAddingRequest] = useState(false);
  const [newReqTitle, setNewReqTitle] = useState("");
  const [newReqNotes, setNewReqNotes] = useState("");
  const [showTrash, setShowTrash] = useState(false);
  const { user } = useAuth();
  const canManage = user?.role === "manager" || user?.role === "admin"; // delete, approve/reject, trash
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [listDragOver, setListDragOver] = useState(false);

  const foldersKey = ["document-folders", accountId];
  const docsKey    = ["client-documents", accountId, selectedPath ?? ""];
  const reqsKey    = ["document-requests", accountId];

  const { data: r2Status } = useQuery({
    queryKey: ["r2-status"],
    queryFn: () => fetchWithAuth<{ configured: boolean }>("/api/r2/status"),
    staleTime: 60_000,
  });

  const { data: flatFolders = [], isLoading: foldersLoading } = useQuery({
    queryKey: foldersKey,
    queryFn: () => getDocumentFolders(accountId),
    staleTime: 30_000,
  });

  const { data: documents = [], isLoading: docsLoading } = useQuery({
    queryKey: docsKey,
    queryFn: () => getClientDocuments(accountId, selectedPath ?? undefined),
    staleTime: 30_000,
  });

  const { data: requests = [] } = useQuery({
    queryKey: reqsKey,
    queryFn: () => getDocumentRequests(accountId),
    staleTime: 30_000,
  });

  const folderTree = useMemo(() => buildTree(flatFolders), [flatFolders]);

  // v1.6.1: delete requests. Managers/admins see all; everyone else sees their own.
  const deleteReqsKey = ["document-delete-requests", accountId];
  const { data: deleteRequests = [] } = useQuery({
    queryKey: deleteReqsKey,
    queryFn: () => getDeleteRequests(accountId),
    staleTime: 15_000,
  });
  const pendingDeleteDocIds = useMemo(
    () => new Set(deleteRequests.filter((r) => r.status === "pending").map((r) => r.document_id)),
    [deleteRequests],
  );
  function refreshDeleteRequests() {
    qc.invalidateQueries({ queryKey: deleteReqsKey });
    qc.invalidateQueries({ queryKey: ["document-trash", accountId] });
    qc.invalidateQueries({ queryKey: ["client-documents", accountId] });
  }

  // v1.5.101: deleted documents go to trash; managers and admins can restore them.
  const { data: trash = [] } = useQuery({
    queryKey: ["document-trash", accountId],
    queryFn: () => getDocumentTrash(accountId),
    enabled: canManage && showTrash,
  });
  async function handleRestore(docId: string, name: string) {
    try {
      await restoreClientDocument(accountId, docId);
      qc.invalidateQueries({ queryKey: ["document-trash", accountId] });
      invalidateDocs();
      toast({ title: "Restored", description: name });
    } catch (err: any) {
      toast({ title: "Restore failed", description: err?.message, variant: "destructive" });
    }
  }

  // Build file count map from ALL documents
  const { data: allDocs = [] } = useQuery({
    queryKey: ["client-documents", accountId, ""],
    queryFn: () => getClientDocuments(accountId),
    staleTime: 60_000,
  });
  const fileCountMap = useMemo(() => {
    const m = new Map<string, number>();
    allDocs.forEach((d) => m.set(d.folder_path, (m.get(d.folder_path) ?? 0) + 1));
    return m;
  }, [allDocs]);

  function invalidateDocs() {
    qc.invalidateQueries({ queryKey: ["client-documents", accountId] });
  }
  function invalidateFolders() {
    qc.invalidateQueries({ queryKey: foldersKey });
  }

  async function handleDeleteFolder(folder: DocumentFolder) {
    try {
      await deleteDocumentFolder(accountId, folder.id);
      if (selectedPath?.startsWith(folder.path)) setSelectedPath(null);
      invalidateFolders();
      invalidateDocs();
      toast({ title: "Folder deleted", description: folder.name });
    } catch (err: any) {
      toast({ title: "Cannot delete folder", description: err?.message, variant: "destructive" });
    } finally {
      setDeletingFolder(null);
    }
  }

  async function startUpload(files: File[], folderPath: string) {
    if (files.length === 0 || uploading) return;
    setUploading(true);
    try {
      await uploadDocumentFiles(accountId, folderPath, files, (done, total) => setProgress({ done, total }));
    } finally {
      setUploading(false);
      setProgress(null);
      invalidateDocs();
    }
  }

  // Stray file drops anywhere else on the tab must not navigate the browser to the file.
  function swallowFileDrag(e: DragEvent<HTMLElement>) {
    if (!isFileDrag(e.dataTransfer.types)) return;
    e.preventDefault();
    if (e.type === "dragover") e.dataTransfer.dropEffect = "none";
  }

  function handleListDragOver(e: DragEvent<HTMLElement>) {
    if (!isFileDrag(e.dataTransfer.types)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    if (!listDragOver) setListDragOver(true);
  }
  function handleListDragLeave(e: DragEvent<HTMLElement>) {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setListDragOver(false);
  }
  function handleListDrop(e: DragEvent<HTMLElement>) {
    if (!isFileDrag(e.dataTransfer.types)) return;
    e.preventDefault();
    e.stopPropagation();
    setListDragOver(false);
    startUpload(filesFromList(e.dataTransfer.files), selectedPath ?? "General");
  }

  const pendingReqs = requests.filter((r) => r.status === "pending");
  const selectedFolderName = selectedPath
    ? (selectedPath.split("/").pop() ?? selectedPath)
    : "All Documents";

  const hasNoFolders = !foldersLoading && flatFolders.length === 0;

  async function createRequest() {
    if (!newReqTitle.trim()) return;
    try {
      await createDocumentRequest(accountId, {
        title: newReqTitle.trim(),
        notes: newReqNotes.trim() || undefined,
        folderPath: "Client Uploads",
      });
      setNewReqTitle(""); setNewReqNotes(""); setAddingRequest(false);
      qc.invalidateQueries({ queryKey: reqsKey });
    } catch (err: any) {
      toast({ title: "Failed to create request", description: err?.message, variant: "destructive" });
    }
  }

  return (
    <div className="flex gap-0 h-full min-h-[500px]" onDragOver={swallowFileDrag} onDrop={swallowFileDrag}>
      {/* ── Left: folder tree ─────────────────────────────── */}
      <div className="w-56 shrink-0 border-r flex flex-col">
        {/* All Documents */}
        <button
          onClick={() => setSelectedPath(null)}
          className={cn(
            "flex items-center gap-2 px-3 py-2 text-sm border-b transition-colors",
            selectedPath === null
              ? "bg-primary/10 text-primary font-medium"
              : "hover:bg-slate-50 text-muted-foreground hover:text-foreground"
          )}
        >
          <FolderOpen className="w-3.5 h-3.5 text-slate-400" />
          All Documents
          {allDocs.length > 0 && (
            <span className="ml-auto text-[10px] text-muted-foreground">{allDocs.length}</span>
          )}
        </button>

        {/* Tree */}
        <div className="flex-1 overflow-y-auto py-1">
          {foldersLoading ? (
            <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />Loading…
            </div>
          ) : hasNoFolders ? null : (
            folderTree.map((f) => (
              <FolderNode
                key={f.id}
                folder={f}
                level={0}
                selectedPath={selectedPath}
                onSelect={setSelectedPath}
                fileCountMap={fileCountMap}
                onAddChild={(parent) => setNewFolderParent(parent)}
                onAddYear={(folder) => setAddYearFolder(folder)}
                onDelete={(folder) => setDeletingFolder(folder)}
                onDropFiles={startUpload}
              />
            ))
          )}
        </div>

        {/* Add top-level folder */}
        <div className="border-t p-2">
          <button
            onClick={() => setNewFolderParent("root")}
            className="flex items-center gap-1.5 w-full text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-slate-100 transition-colors"
          >
            <Plus className="w-3 h-3" />New folder
          </button>
        </div>
      </div>

      {/* ── Right: file list ──────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {hasNoFolders ? (
          <ScaffoldPrompt accountId={accountId} onDone={invalidateFolders} />
        ) : (
          <>
            {/* R2 banner */}
            {r2Status && !r2Status.configured && (
              <div className="p-3">
                <R2Banner />
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 min-w-0">
                {selectedPath ? (
                  <>
                    <button onClick={() => setSelectedPath(null)}
                      className="text-muted-foreground hover:text-foreground text-xs">All</button>
                    {selectedPath.split("/").map((seg, i, arr) => (
                      <span key={i} className="flex items-center gap-1">
                        <ChevronRight className="w-3 h-3 text-muted-foreground/50" />
                        <button
                          onClick={() => setSelectedPath(arr.slice(0, i + 1).join("/"))}
                          className={cn("hover:text-foreground", i === arr.length - 1 ? "text-foreground" : "text-muted-foreground text-xs")}
                        >{seg}</button>
                      </span>
                    ))}
                  </>
                ) : (
                  <span>All Documents</span>
                )}
              </div>
              <UploadButton
                uploading={uploading}
                progress={progress}
                onFiles={(files) => startUpload(files, selectedPath ?? "General")}
              />
            </div>

            {/* Files (drop target: uploads into the selected folder) */}
            <div
              data-drop-target="list"
              className="flex-1 overflow-y-auto relative"
              onDragOver={handleListDragOver}
              onDragLeave={handleListDragLeave}
              onDrop={handleListDrop}
            >
              {listDragOver && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-primary bg-primary/10 pointer-events-none rounded">
                  <Upload className="w-8 h-8 text-primary" />
                  <p className="text-sm font-medium text-primary">Drop files to upload to {selectedFolderName === "All Documents" ? "General" : selectedFolderName}</p>
                </div>
              )}
              {docsLoading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />Loading…
                </div>
              ) : documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                  <FolderOpen className="w-8 h-8 opacity-20" />
                  <p className="text-sm">No files in {selectedFolderName}</p>
                  <p className="text-xs opacity-60">Click Upload to add files here</p>
                </div>
              ) : (
                <div className="border-t">
                  {documents.map((doc) => (
                    <DocumentRow key={doc.id} doc={doc} accountId={accountId} folders={flatFolders}
                      onDeleted={invalidateDocs} onUpdated={invalidateDocs}
                      showFolder={selectedPath === null}
                      canDelete={canManage}
                      hasPendingDelete={pendingDeleteDocIds.has(doc.id)}
                      onDeleteRequested={refreshDeleteRequests} />
                  ))}
                </div>
              )}
            </div>

            {/* Document requests */}
            <div className="border-t">
              <button
                onClick={() => setShowRequests((v) => !v)}
                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-slate-50"
              >
                <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", !showRequests && "-rotate-90")} />
                Document Requests
                {pendingReqs.length > 0 && (
                  <span className="ml-1 bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                    {pendingReqs.length} pending
                  </span>
                )}
              </button>

              {showRequests && (
                <div className="px-4 pb-4 flex flex-col gap-2">
                  {requests.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No requests yet</p>
                  ) : (
                    requests.map((req) => (
                      <RequestRow key={req.id} request={req} accountId={accountId}
                        onUpdated={() => qc.invalidateQueries({ queryKey: reqsKey })}
                        onDeleted={() => qc.invalidateQueries({ queryKey: reqsKey })} />
                    ))
                  )}

                  {addingRequest ? (
                    <div className="border rounded-lg p-3 bg-slate-50 flex flex-col gap-2">
                      <input autoFocus className="border rounded px-2 py-1 text-sm w-full bg-white"
                        placeholder="What document are you requesting?"
                        value={newReqTitle} onChange={(e) => setNewReqTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") createRequest(); if (e.key === "Escape") setAddingRequest(false); }} />
                      <input className="border rounded px-2 py-1 text-xs bg-white"
                        placeholder="Notes (optional)"
                        value={newReqNotes} onChange={(e) => setNewReqNotes(e.target.value)} />
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7 text-xs" onClick={createRequest} disabled={!newReqTitle.trim()}>
                          Create Request
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAddingRequest(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5 w-fit"
                      onClick={() => setAddingRequest(true)}>
                      <Plus className="w-3 h-3" />New Request
                    </Button>
                  )}
                </div>
              )}
            </div>

            {deleteRequests.length > 0 && (
              <div className="border-t">
                <div className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground">
                  Delete requests
                  {pendingDeleteDocIds.size > 0 && (
                    <span className="bg-amber-100 text-amber-800 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                      {pendingDeleteDocIds.size} pending
                    </span>
                  )}
                </div>
                <div className="px-4 pb-4 flex flex-col gap-2">
                  {deleteRequests.map((r) => (
                    <DeleteRequestRow key={r.id} request={r} canManage={canManage}
                      isMine={r.requested_by === user?.id} onChanged={refreshDeleteRequests} />
                  ))}
                </div>
              </div>
            )}

            {canManage && (
              <div className="border-t">
                <button
                  onClick={() => setShowTrash((v) => !v)}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-slate-50"
                >
                  <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", !showTrash && "-rotate-90")} />
                  Trash
                </button>
                {showTrash && (
                  <div className="px-4 pb-4 flex flex-col gap-1">
                    {trash.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Trash is empty</p>
                    ) : trash.map((d) => (
                      <div key={d.id} className="flex items-center gap-2 text-sm py-1">
                        <span className="flex-1 min-w-0 truncate" title={d.file_name}>{d.file_name}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[30%]">{d.folder_path}</span>
                        <span className="text-xs text-muted-foreground">{format(new Date(d.deleted_at), "MMM d, yyyy")}</span>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleRestore(d.id, d.file_name)}>
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add year dialog */}
      {addYearFolder && (
        <AddYearDialog
          accountId={accountId}
          folder={addYearFolder}
          onCreated={invalidateFolders}
          onClose={() => setAddYearFolder(null)}
        />
      )}

      {/* New folder dialog */}
      {newFolderParent !== null && (
        <NewFolderDialog
          accountId={accountId}
          parent={newFolderParent === "root" ? null : newFolderParent}
          onCreated={invalidateFolders}
          onClose={() => setNewFolderParent(null)}
        />
      )}

      {/* Delete folder confirmation */}
      {deletingFolder && (
        <Dialog open onOpenChange={() => setDeletingFolder(null)}>
          <DialogContent className="max-w-sm">
            <div className="flex flex-col gap-3 p-1">
              <p className="text-sm font-semibold">Delete "{deletingFolder.name}"?</p>
              <p className="text-xs text-muted-foreground">
                This will delete the folder and all its subfolders. Files inside cannot be in a deleted folder — if any files are stored here this will fail.
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="destructive" onClick={() => handleDeleteFolder(deletingFolder)}>
                  Delete
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeletingFolder(null)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
